import clsx from 'clsx';
import {
  AuditOutlined,
  CalendarOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FileAddOutlined,
  UsergroupAddOutlined
} from '@ant-design/icons';
import { CardItem } from 'components/professor_area/CardItem';
import { EVALUATION_KIND_MAP } from 'services/graduate-enrollment/evaluations';
import { MenuInfo } from 'rc-menu/lib/interface';
import { Space, Tag, Typography } from 'antd';
import { formatDate } from 'services/date';
import { kindMap } from 'components/classroom/Evaluation';
import { useCallback, useMemo } from 'react';
import { useNavigation } from '@refinedev/core';
import { useParams } from 'react-router-dom';
import type { IProfessorAreaEvaluationsList } from 'interfaces/professor_area/evaluations';
import type { MenuProps } from 'antd/lib';

const { Text } = Typography;

interface EvaluationsListItemProps {
  evaluation: IProfessorAreaEvaluationsList;
  onEdit: (evaluation: IProfessorAreaEvaluationsList) => void;
  onRemove: (id: string) => void;
  onAddReplacement: (evaluation: IProfessorAreaEvaluationsList) => void;
  onAssociateStudents: (evaluation: IProfessorAreaEvaluationsList) => void;
  canShow: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canCreate: boolean;
  canAssociateStudents: boolean;
  className?: string;
}

export const EvaluationsListItem = ({
  evaluation,
  onEdit,
  onRemove,
  onAddReplacement,
  onAssociateStudents,
  canShow,
  canEdit,
  canDelete,
  canCreate,
  canAssociateStudents,
  className
}: EvaluationsListItemProps) => {
  const { show } = useNavigation();
  const params = useParams();
  const classroomId = params.id;

  const canHaveReplacement =
    evaluation.kind === 'regular' || evaluation.kind === 'final';

  const canAssociateReplacementStudents =
    evaluation.kind === 'replacement' &&
    evaluation.rule === 'classroom_enrollment';

  const items = useMemo<MenuProps['items']>(() => {
    const base: MenuProps['items'] = [];

    if (canShow) {
      base.push({
        key: 'show',
        label: 'Visualizar avaliação',
        icon: <EyeOutlined />
      });
    }

    if (canEdit) {
      base.push({
        key: 'edit',
        label: 'Editar avaliação',
        icon: <EditOutlined />
      });
    }

    if (canHaveReplacement && canCreate) {
      base.push({
        key: 'replacement',
        label: 'Adicionar substitutiva',
        icon: <FileAddOutlined />
      });
    }

    if (canAssociateReplacementStudents && canAssociateStudents) {
      base.push({
        key: 'associate',
        label: 'Associar alunos',
        icon: <UsergroupAddOutlined />
      });
    }

    if (canDelete) {
      base.push({
        key: 'delete',
        label: 'Excluir avaliação',
        icon: <DeleteOutlined />
      });
    }

    return base;
  }, [
    canAssociateReplacementStudents,
    canAssociateStudents,
    canCreate,
    canDelete,
    canEdit,
    canHaveReplacement,
    canShow
  ]);

  const handleDropdown = useCallback(
    (event: MenuInfo) => {
      switch (event.key) {
        case 'show': {
          show('professor_area/classroom_assessments', evaluation.id, 'push', {
            classroomId
          });
          break;
        }

        case 'edit': {
          onEdit(evaluation);
          break;
        }

        case 'replacement': {
          onAddReplacement(evaluation);
          break;
        }

        case 'associate': {
          onAssociateStudents(evaluation);
          break;
        }

        case 'delete': {
          onRemove(evaluation.id);
          break;
        }
      }
    },
    [
      show,
      evaluation,
      classroomId,
      onEdit,
      onRemove,
      onAddReplacement,
      onAssociateStudents
    ]
  );

  const evaluationDate = useMemo(() => {
    const rules = evaluation.requirements?.[0]?.rules ?? [];

    const starts_at =
      rules.find(rule => rule.operator === 'after')?.value || '';
    const ends_at = rules.find(rule => rule.operator === 'before')?.value || '';

    return { starts_at, ends_at };
  }, [evaluation.requirements]);

  const menuProps: MenuProps = {
    items,
    onClick: handleDropdown
  };

  return (
    <CardItem
      icon={AuditOutlined}
      title={evaluation.name}
      menuProps={menuProps}
      className={clsx('!py-2 !px-2', className)}
      meta={
        <>
          <Text>
            {kindMap[evaluation.kind as keyof typeof kindMap] ??
              evaluation.kind}
          </Text>
          <Text>{EVALUATION_KIND_MAP[evaluation.evaluation_kind]}</Text>
          {evaluation.kind === 'regular' && (
            <Space size="small">
              <Text type="secondary">Peso</Text>
              <Text>{evaluation.weight ?? 0}</Text>
            </Space>
          )}
          <Space size="small">
            <CalendarOutlined />
            <Text>
              {formatDate(evaluationDate.starts_at, 'DD/MM/YYYY')}
              {' > '}
              {formatDate(evaluationDate.ends_at, 'DD/MM/YYYY')}
            </Text>
          </Space>
        </>
      }
      extraContent={
        <Tag
          color={
            evaluation.pending_evaluation_attempts_count > 0
              ? 'orange'
              : 'success'
          }
        >
          {evaluation.pending_evaluation_attempts_count} avaliações a corrigir
        </Tag>
      }
    />
  );
};
